import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "selectBox" }
const _hoisted_2 = ["onClick"]
const _hoisted_3 = ["onClick"]
const _hoisted_4 = ["onClick"]

import { onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { months } from '@/helpers/months';
import { SelectModel, YearModel } from '@/models/sidebar/select.model';


export default /*@__PURE__*/_defineComponent({
  __name: 'Select',
  props: {
    start: {},
    end: {}
  },
  setup(__props: any) {

dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(timezone);
dayjs.extend(utc);

const { t } = useI18n();

const props = __props;
const store = useStore();

const year = ref<number | null>(null);
const month = ref<string | null>(null);
const day = ref<number | null>(null);
const unexpand = ({
  year: null as number | null,
  month: null as string | null,
  day: null as number | null,
});

// While loop which adds one month until its filled with the dates, from the starting date to the ending date
const getDates = (): YearModel[] => {
  const startDate = dayjs.tz(props.start, 'Asia/Jerusalem');
  const endDate = dayjs.tz(props.end, 'Asia/Jerusalem');
  const years: YearModel[] = [];

  let currentDate = startDate.startOf('month');
  const lastMonth = endDate.startOf('month');

  while (currentDate.isSameOrBefore(lastMonth, 'month')) {
    const currentYear = currentDate.year();
    let yearObj = years.find(y => y.currentYear === currentYear);

    if (!yearObj) {
      yearObj = { currentYear, months: [] };
      years.push(yearObj);
    }

    const isStartMonth = currentDate.year() === startDate.year() && currentDate.month() === startDate.month();
    const isEndMonth = currentDate.year() === endDate.year() && currentDate.month() === endDate.month();

    const startDay = isStartMonth ? startDate.date() : 1;
    const endDay = isEndMonth ? endDate.date() : currentDate.daysInMonth();

    const days = Array.from(
      { length: endDay - startDay + 1 },
      (_, i) => startDay + i
    );

    yearObj.months.push({
      month: currentDate.locale('en').format('MMMM'),
      clientMonth: currentDate.locale(store.state.locale).format('MMMM'),
      days
    });

    currentDate = currentDate.add(1, 'month');
  }

  return years;
};

const setUnexpand = () => {
  unexpand.year = year.value;
  unexpand.month = month.value;
  unexpand.day = day.value;
};

const resetUnexpand = () => {
  unexpand.year = null;
  unexpand.month = null;
  unexpand.day = null;
};

const selectYear = (currentYear: number) => {
  if (year.value === currentYear) {
    setUnexpand();
    year.value = null;
    month.value = null;
    day.value = null;
    return;
  }
  year.value = currentYear;
  month.value = (currentYear === unexpand.year) ? unexpand.month : null;
  day.value = unexpand.day;
};

const selectMonth = (currentYear: number, currentMonth: string) => {
  if (month.value === currentMonth) {
    setUnexpand();
    month.value = null;
    day.value = null;
    return;
  }
  year.value = currentYear;
  month.value = currentMonth;
  day.value = (currentYear === unexpand.year && currentMonth === unexpand.month) ? unexpand.day : null;
  resetUnexpand();
};

const selectDay = (currentYear: number, currentMonth: string, currentDay: number) => {
  year.value = currentYear;
  month.value = currentMonth;
  day.value = currentDay;

  const m = monthToNumber(month.value);
  const date = new Date(year.value, m, day.value + 1);
  const dateString = date.toISOString().split('T')[0];
  store.commit('setDate', dateString);
};

// Sets the date to the current selected date
const initDate = () => {
  const date = dayjs.tz(store.state.date, 'Asia/Jerusalem');
  if (unexpand.year) {
    unexpand.year = date.year();
  }
  else {
    year.value = date.year();
  }
  if (unexpand.month) {
    unexpand.month = date.locale('en').format('MMMM');
  }
  else {
    month.value = date.locale('en').format('MMMM');
  }
  day.value = date.date();
};

// Adds a 0- to the date if it's smaller than 10 (for example 09-06-1945)
const prettyDay = (currentDay: number) => {
  return (currentDay < 10) ? `0${currentDay}` : currentDay;
};

// Converts month to a number based on its index
// TODO: Check if this function is necessary
const monthToNumber = (currentMonth: string) => {
  return months.indexOf(currentMonth);
};

// Gets the title of the event from the locale files
const getEventTitle = (currentYear: number, currentMonth: string, currentDay: number) => {
  const m = monthToNumber(currentMonth);
  const date = new Date(currentYear, m, currentDay + 1);
  const dateString = date.toISOString().split('T')[0];
  const title = t(`dateTitle.${dateString}`);
  return title || '';
};

onMounted(() => {
  initDate();
  const unsubscribe = store.watch(
    (state) => state.date,
    (newValue, oldValue) => {
      if (newValue !== oldValue) {
        initDate();
      }
    }
  );

  onUnmounted(() => {
    unsubscribe();
  });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getDates(), (currentYear, yearIndex) => {
      return (_openBlock(), _createElementBlock(_Fragment, { key: yearIndex }, [
        _createElementVNode("div", {
          class: _normalizeClass(["selectBox-year", { 'selectBox-active': currentYear.currentYear === year.value }]),
          onClick: ($event: any) => (selectYear(currentYear.currentYear))
        }, _toDisplayString(currentYear.currentYear), 11, _hoisted_2),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentYear.months, (currentMonth, monthIndex) => {
          return (_openBlock(), _createElementBlock(_Fragment, { key: monthIndex }, [
            (year.value === currentYear.currentYear)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["selectBox-month", { 'selectBox-active': month.value === currentMonth.month }]),
                  onClick: ($event: any) => (selectMonth(currentYear.currentYear, currentMonth.month))
                }, _toDisplayString(currentMonth.clientMonth), 11, _hoisted_3))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentMonth.days, (currentDay, dayIndex) => {
              return (_openBlock(), _createElementBlock(_Fragment, { key: dayIndex }, [
                (month.value === currentMonth.month && year.value === currentYear.currentYear)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: _normalizeClass(["selectBox-day", { 'selectBox-active': day.value === currentDay }]),
                      onClick: ($event: any) => (selectDay(currentYear.currentYear, currentMonth.month, currentDay))
                    }, _toDisplayString(prettyDay(currentDay)) + " - " + _toDisplayString(getEventTitle(currentYear.currentYear, currentMonth.month, currentDay)), 11, _hoisted_4))
                  : _createCommentVNode("", true)
              ], 64))
            }), 128))
          ], 64))
        }), 128))
      ], 64))
    }), 128))
  ]))
}
}

})